import { Box, Grid, IconButton, Theme, Typography, useMediaQuery } from "@material-ui/core";
import { createContext, useEffect, useState } from "react";
import { Title, useRedirect } from "react-admin";
import { useDispatch, useSelector } from "react-redux";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";

import ChatIcon from "../../assets/chat.svg";
import CustomCard from "../../components/CustomCard";
import ImageWithText from "../../components/ImageWithText";
import useCurrentUser from "../../hooks/useCurrentUser";
import { DocReference } from "../../types/firebase.types";
import { ReduxState } from "../../types/redux.types";
import { AppResource, ParticipantType } from "../../types/resource.types";
import { UserRole } from "../../types/user.types";
import ChatRooms from "./components/ChatRooms";
import Chatting from "./components/Chatting";
import { getChatRoomsAction } from "./redux/actions";
import { IBaseChatContext, IRoomHeader } from "./type";
import { getCurrentUserRef } from "./utils";
import chatQueries from "./utils/chatQueries";
import useSyncChatRooms from "./utils/useSyncChatRooms";
import colors from "../../theme/colors";
import { useParams } from "react-router-dom";

export const BaseChatContext = createContext<IBaseChatContext>({
  userRef: undefined,
  senderRef: null,
  userId: undefined,
});

const Chat = () => {
  const dispatch = useDispatch();
  const roomsHeader = useSelector<ReduxState, IRoomHeader[]>((state) => state.chat.roomsHeader);
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const isChatRooms = useParams<{ roomId: string }>();
  const redirect = useRedirect();
  const [chatResponsive, setChatResponsive] = useState(false);
  const [clientDetails, setClientDetails] = useState({ name: "", photoUrl: "" });

  const { user } = useCurrentUser();
  useSyncChatRooms();

  const redirectRoomsInResponsive = () => {
    setChatResponsive(false);
    redirect(`/${AppResource.chat}/rooms`);
  };

  useEffect(() => {
    dispatch(
      getChatRoomsAction({
        getChatRoomsQuery: () =>
          chatQueries.getChatRooms({
            userId: user.id as string,
            type:
              user.role === UserRole.admin
                ? ParticipantType.admin
                : user.role === UserRole.sportClub
                ? ParticipantType.sportsClubs
                : user.role === UserRole.store
                ? ParticipantType.brands
                : user.role === UserRole.academy
                ? ParticipantType.academies
                : user.role === UserRole.coach
                ? ParticipantType.coaches
                : user.role === UserRole.gym
                ? ParticipantType.gyms
                : ParticipantType.entertainments,
          }),
        userId: user.id as string,
      })
    );
  }, []);

  useEffect(() => {
    if (isSm && isChatRooms.roomId === AppResource.rooms) setChatResponsive(false);
  }, [isChatRooms.roomId]);

  return (
    <>
      <Title title="Chat" />
      <BaseChatContext.Provider
        value={{
          userRef: getCurrentUserRef(user.role, user.id as string) as DocReference,
          senderRef: getCurrentUserRef(user.role, user.id as string),
          userId: user.id,
        }}
      >
        <Box height={isSm ? "80vh" : "90vh"}>
          {isSm && chatResponsive && (
            <Box display="flex" alignItems="center">
              <IconButton onClick={redirectRoomsInResponsive}>
                <KeyboardBackspaceIcon style={{ color: colors.success.main }} />
              </IconButton>
              <Box display="flex" alignItems="center">
                {clientDetails.photoUrl && (
                  <img
                    src={clientDetails.photoUrl}
                    alt={clientDetails.name}
                    style={{
                      width: 35,
                      height: 35,
                      borderRadius: "50%",
                      marginRight: 8,
                    }}
                  />
                )}
                <Typography>{clientDetails.name}</Typography>
              </Box>
            </Box>
          )}
          <CustomCard padding={0}>
            {roomsHeader.length > 0 ? (
              <Grid container style={{ height: "inherit" }}>
                {!isSm ? (
                  <>
                    <Grid item md={4} sm={12} style={{ width: isSm ? "100%" : "" }}>
                      <ChatRooms />
                    </Grid>
                    <Grid item md={8} sm={12} style={{ width: isSm ? "100%" : "" }}>
                      <Chatting />
                    </Grid>
                  </>
                ) : (
                  <>
                    {isSm && !chatResponsive && (
                      <Grid item md={4} sm={12} style={{ width: isSm ? "100%" : "" }}>
                        <ChatRooms
                          setChatResponsive={setChatResponsive}
                          getClientDetails={(name: string, photoUrl: string) => setClientDetails({ name, photoUrl })}
                        />
                      </Grid>
                    )}
                    {isSm && chatResponsive && (
                      <Grid item md={4} sm={12} style={{ width: isSm ? "100%" : "" }}>
                        <Chatting />
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            ) : (
              <ImageWithText src={ChatIcon} message="No chats yet!" />
            )}
          </CustomCard>
        </Box>
      </BaseChatContext.Provider>
    </>
  );
};

export default Chat;
